import { Controller } from "stimulus";

export default class DrawFlowController extends Controller {

    get nodeGap() { return 130; }

    initialize() {
        super.initialize();
        this.autoSaveTimer = null;
        this.campaignId = this.getCampaignId();
        document.addEventListener("click", this.hideActionMenu);
    }

    connect() {
        this.setupDrawFlow();
        this.hoistModals();
        this.setupAutoSave();
    };

    disconnect() {
        clearInterval(this.autoSaveTimer);
        this.autoSaveTimer = null;
    }



    getCampaignId() {
        let path = location.pathname;
        let match = path.match(/\/campaign\/([^\/]+)\/edit/)
        if (!match) return null;

        return match[1];
    }

    hoistModals() {
        const modal = document.getElementById('email-template-modal');
        if (modal) {
            document.body.appendChild(modal);
            modal.addEventListener('click', (e) => {
                const card = e.target.closest('.template-card');
                if (card) this.selectEmailTemplate({ stopPropagation() {}, currentTarget: card });
            });
        }
        const triggerModal = document.getElementById('trigger-modal');
        if (triggerModal) document.body.appendChild(triggerModal);

        const upgradeModal = document.getElementById('upgrade-to-pro-modal');
        if (upgradeModal) document.body.appendChild(upgradeModal);
    }

    get smsRestricted() {
        return Boolean(document.querySelector('[data-type="send-sms"][data-pro-feature="true"]'));
    }

    hasSmsNode() {
        const data = this.editor?.drawflow?.drawflow?.Home?.data;
        if (!data) return false;
        return Object.values(data).some(node => node?.name === 'send-sms');
    }

    showUpgradeToProModal() {
        $('#upgrade-to-pro-modal').modal('show');
    }

    setupDrawFlow() {
        let container = document.getElementById("flow-canvas");
        const editor = new Drawflow(container);
        this.editor = editor;
        editor.curvature = 0.5;
        editor.line_path = 15;
        editor.reroute = true;
        editor.force_curve = true;
        editor.line_path = 2; // 2 = top-bottom bezier

        editor.start();
        editor.createCurvature = this.editorCreateCurvature;
        let sequence = document.querySelector('#sequence')?.value
        if (Boolean(sequence)) {
            editor.import(JSON.parse(sequence));
            this.refreshSenderEmailOptions();
        } else {
            const posX = editor.precanvas.clientWidth / 2;
            const posY = editor.precanvas.getBoundingClientRect().y;
            const triggerId = this.addNodeToDrawFlow("trigger", posX, posY);
            const conditionId = this.addNodeToDrawFlow("condition", posX, posY + (1.5 * this.nodeGap));
            const emailId = this.addNodeToDrawFlow("send-email", posX - 250, posY + 3 * this.nodeGap);
            // Skip seeding the SMS branch for non-Pro accounts so the page
            // doesn't load the upgrade modal unprompted; the user can still
            // drag the (locked) Send SMS item in to trigger the upsell.
            const smsId = this.smsRestricted
                ? this.addNodeToDrawFlow("send-email", posX + 250, posY + 3 * this.nodeGap)
                : this.addNodeToDrawFlow("send-sms", posX + 250, posY + 3 * this.nodeGap);
            const endId = this.addNodeToDrawFlow("end", posX + 105, posY + 5.8 * this.nodeGap);

            editor.addConnection(triggerId, conditionId, "output_1", "input_1");
            editor.addConnection(conditionId, emailId, "output_1", "input_1");
            editor.addConnection(conditionId, smsId, "output_2", "input_1");
            editor.addConnection(emailId, endId, "output_1", "input_1");
            editor.addConnection(smsId, endId, "output_1", "input_1");
            this.refreshConnections();
        }

        this.editorSetupEvents();
        this.setupDragEvent();
        this.setupConnectionClickHandler();
        this.setupErrorAutoClear();
        this.syncAllTriggers();
        // Drawflow snapshots each node's outerHTML on save, which means
        // any error classes still on the DOM at save time round-trip into
        // the persisted sequence. Strip them on load so a user who saved
        // a draft with errors doesn't reopen the campaign with stale red
        // borders and no badge / popover to explain them.
        this.applyNodeErrors(new Map());
    }

    // Wires a delegated input/change listener on the canvas so that when a
    // user fills in a previously-empty required field, the corresponding
    // error drops off the node's badge immediately — no need to wait for
    // the next Save attempt. The matching logic lives in
    // handleNodeInputForErrorClear; this is just the hook-up.
    setupErrorAutoClear() {
        if (!this.editor?.precanvas) return;
        this._errorClearHandler = (e) => this.handleNodeInputForErrorClear(e.target);
        this.editor.precanvas.addEventListener('input', this._errorClearHandler, true);
        this.editor.precanvas.addEventListener('change', this._errorClearHandler, true);
    }

    editorCreateCurvature(xstart, ystart, xend, yend, curvature, type) {
        const dist = Math.abs(yend - ystart);
        const isReverse = ystart >= yend;

        // Default multipliers for the curvature
        let m1 = 1;
        let m2 = 1;

        // Only the 'reverse' direction (bottom-to-top) needs special multiplier logic
        if (isReverse) {
            switch (type) {
                case 'open':  m1 = 1;  m2 = -1; break;
                case 'close': m1 = -1; m2 = 1;  break;
                case 'other': m1 = -1; m2 = -1; break;
            }
        }

        const hy1 = ystart + dist * (curvature * m1);
        const hy2 = yend - dist * (curvature * m2);

        return `M ${xstart} ${ystart} C ${xstart} ${hy1} ${xend} ${hy2} ${xend} ${yend}`;
    }

    editorSetupEvents() {
        const controller = this;
        const editor = this.editor;

        // Events!
        editor.on('nodeCreated', function(id) {
            console.log("Node created " + id);
            controller.updateSequenceJson();
            controller.scheduleAutosave();
        })

        editor.on('nodeRemoved', function(id) {
            console.log("Node removed " + id);
            controller.updateSequenceJson();
            controller.scheduleAutosave();
        })

        editor.on('nodeSelected', function(id) {
            console.log("Node selected " + id);
        })

        editor.on('moduleCreated', function(name) {
            console.log("Module Created " + name);
        })

        editor.on('moduleChanged', function(name) {
            console.log("Module Changed " + name);
        })

        editor.on('connectionCreated', function(connection) {
            const nodes = editor.drawflow.drawflow.Home.data;

            const outputConns = nodes[connection.output_id]?.outputs[connection.output_class]?.connections || [];
            const inputConns = nodes[connection.input_id]?.inputs[connection.input_class]?.connections || [];
            const inputNodeIsEnd = nodes[connection.input_id]?.name === 'end';

            if (outputConns.length > 1 || (!inputNodeIsEnd && inputConns.length > 1)) {
                editor.removeSingleConnection(connection.output_id, connection.input_id, connection.output_class, connection.input_class);
                return;
            }

            controller.updateSequenceJson();
            controller.scheduleAutosave();
        })

        editor.on('connectionRemoved', function(connection) {
            controller.updateSequenceJson();
            controller.scheduleAutosave();
        })

        editor.on('mouseMove', function(position) {
            // console.log('Position mouse x:' + position.x + ' y:'+ position.y);
        })

        editor.on('nodeMoved', function(id) {
            console.log("Node moved " + id);
        })

        editor.on('zoom', function(zoom) {
            document.querySelector(".zoom-level").innerHTML = Math.round(zoom * 100) + '%';
        })

        editor.on('translate', function(position) {
            console.log('Translate x:' + position.x + ' y:'+ position.y);
        })

        editor.on('addReroute', function(id) {
            console.log("Reroute added " + id);
        })

        editor.on('removeReroute', function(id) {
            console.log("Reroute removed " + id);
        })
    }

    updateSequenceJson() {
        const sequenceEl = document.querySelector('#sequence');
        if (!sequenceEl) return;

        this.syncDomStateIntoNodeHtml();

        sequenceEl.value = JSON.stringify(this.editor.export());
    }

    syncDomStateIntoNodeHtml() {
        const data = this.editor?.drawflow?.drawflow?.Home?.data;
        if (!data) return;

        Object.keys(data).forEach(id => {
            const wrapper = document.getElementById(`node-${id}`);
            if (!wrapper) return;

            // The node template's root sits inside the drawflow wrapper.
            const root = wrapper.querySelector('.flow-node, .end-node') || wrapper.firstElementChild;
            if (!root) return;

            root.querySelectorAll('input, textarea').forEach(el => {
                if (el.type === 'checkbox' || el.type === 'radio') {
                    if (el.checked) el.setAttribute('checked', '');
                    else el.removeAttribute('checked');
                } else {
                    el.setAttribute('value', el.value ?? '');
                }
            });

            root.querySelectorAll('select').forEach(select => {
                [...select.options].forEach(opt => {
                    if (opt.selected) opt.setAttribute('selected', '');
                    else opt.removeAttribute('selected');
                });
            });

            data[id].html = root.outerHTML;
        });
    }

    // This function traces back from a specific node ID to find all conditions.
    // It walks through every parent (delay, sms, email, tag, etc.) and only
    // records data when the parent is a condition node. Recursion stops at the
    // trigger node or when there are no more inputs.
    getPathToTrigger(nodeId, data) {
        const path = [];
        const currentNode = data[nodeId];
        if (!currentNode) return path;

        const inputConnections = currentNode.inputs?.input_1?.connections || [];
        if (inputConnections.length === 0) return path;

        const parentId = inputConnections[0].node;
        const parentNode = data[parentId];
        if (!parentNode) return path;

        // Reached the start, stop recursion
        if (parentNode.name === 'trigger') return path;

        // Record condition data (branch-aware) when climbing through a condition
        if (parentNode.name === 'condition') {
            path.push(this.getConditionNodeData(parentId, nodeId));
        }

        // Keep climbing regardless of parent type so intermediate delay/sms/email
        // nodes don't break the path back to the trigger.
        return path.concat(this.getPathToTrigger(parentId, data));
    }

    // Helper to extract values from the UI of a condition node.
    // When a childId is provided, detects whether the child is connected to
    // output_1 (Yes) or output_2 (No) and flips the query_type accordingly
    // for operators that have a known paired opposite.
    getConditionNodeData(nodeId, childId = null) {
        const nodeElement = document.querySelector(`#node-${nodeId}`);
        if (!nodeElement) return {};

        const fieldSelect = nodeElement.querySelector(".condition-field-select");
        const selectedOpt = fieldSelect?.options[fieldSelect.selectedIndex];
        const operatorSelect = nodeElement.querySelector(".condition-operator-select");
        const valueInput = nodeElement.querySelector(".condition-value-input");
        const filterType = selectedOpt?.getAttribute("data-filter-type") || "";

        const branch = this.getConditionBranch(nodeId, childId);

        // Duration-range carries a date pair instead of an operator+value. The
        // Yes/No branch flip doesn't apply (there's no paired-opposite for
        // "between") so query_type stays "between" on both branches.
        if (filterType === "duration-range") {
            const startInput = nodeElement.querySelector(".condition-start-date");
            const endInput   = nodeElement.querySelector(".condition-end-date");
            return {
                branch,
                search_field: fieldSelect?.value || "",
                query_type:   "between",
                search_value: "",
                start_date:   startInput?.value || "",
                end_date:     endInput?.value || "",
                filter_type:  filterType
            };
        }

        const rawOperator = operatorSelect?.value || "";
        const queryType = branch === "no"
            ? this.invertConditionOperator(rawOperator)
            : rawOperator;

        return {
            branch,
            search_field: fieldSelect?.value || "",
            query_type: queryType,
            search_value: valueInput?.value || "",
            filter_type: filterType
        };
    }

    // Given a condition node and one of its descendants, figure out which
    // output port (Yes = output_1, No = output_2) connects them. Defaults to "yes".
    getConditionBranch(conditionId, childId) {
        if (childId == null) return "yes";
        const nodes = this.editor?.drawflow?.drawflow?.Home?.data;
        const condition = nodes?.[conditionId];
        if (!condition) return "yes";
        const outputs = condition.outputs || {};
        const childMatches = key =>
            (outputs[key]?.connections || []).some(c => String(c.node) === String(childId));
        if (childMatches("output_1")) return "yes";
        if (childMatches("output_2")) return "no";
        return "yes";
    }

    // Paired-opposite lookup. Operators without a known opposite are returned
    // unchanged; callers can still rely on the `branch` field to disambiguate.
    invertConditionOperator(op) {
        const pairs = {
            "is": "is not",
            "is not": "is",
            "contains": "does not contain",
            "does not contain": "contains",
            "has any value": "is unknown",
            "is unknown": "has any value",
            "true": "false",
            "false": "true"
        };
        return pairs[op] || op;
    }

    // Walks upstream from a node collecting every time-delay node encountered
    // (stops at the trigger). Returned in chronological order (furthest from
    // the node first, closest last) as an array of { value, unit } objects.
    getDelaysForNode(nodeId, data) {
        const delays = [];
        let currentId = nodeId;

        // Cap the loop to avoid infinite walks on malformed graphs
        for (let i = 0; i < 500; i++) {
            const currentNode = data[currentId];
            if (!currentNode) break;

            const inputConnections = currentNode.inputs?.input_1?.connections || [];
            if (inputConnections.length === 0) break;

            const parentId = inputConnections[0].node;
            const parentNode = data[parentId];
            if (!parentNode || parentNode.name === 'trigger') break;

            if (parentNode.name === 'time-delay') {
                const el = document.querySelector(`#node-${parentId}`);
                const value = el?.querySelector('.delay-value')?.value;
                const unit = el?.querySelector('.delay-unit')?.value;
                if (value) delays.push({ value, unit });
            }

            currentId = parentId;
        }

        // Reverse so oldest delay (closest to trigger) comes first
        return delays.reverse();
    }

    // The main action called before saving
    syncAllTriggers() {
        const nodes = this.editor.drawflow.drawflow.Home.data;

        Object.keys(nodes).forEach(id => {
            const node = nodes[id];
            if (!['send-sms', 'send-email'].includes(node.name)) return;

            const fullPath = this.getPathToTrigger(id, nodes);
            const delays = this.getDelaysForNode(id, nodes);

            // Update the hidden inputs in the Drawflow node so they persist to DB
            const nodeEl = document.querySelector(`#node-${id}`);
            if (!nodeEl) return;

            const name = node.name.replace('send-', '');
            const triggerInput = nodeEl.querySelector(`.${name}-trigger`);
            if (triggerInput) triggerInput.value = JSON.stringify(fullPath);

            const delayInput = nodeEl.querySelector(`.${name}-delay`);
            if (delayInput) delayInput.value = delays.length ? JSON.stringify(delays) : '';
        });
    }

    editorZoomIn() {
        this.editor.zoom_in()
    }

    editorZoomOut() {
        this.editor.zoom_out()
    }

    setupDragEvent() {
        /* DRAG EVENT - Mouse and Touch Actions */
        const elements = document.getElementsByClassName('draggable-item');
        for (let i = 0; i < elements.length; i++) {
            elements[i].addEventListener('touchend', this.drop, false);
            elements[i].addEventListener('touchstart', this.drag, false );
        }
    }

    allowDrop(ev) {
        ev.preventDefault();
    }

    drag(ev) {
        const item = ev.target.closest('.draggable-item') || ev.currentTarget;
        ev.dataTransfer.setData("type", item.getAttribute('data-type'));
    }

    drop(ev) {
        ev.preventDefault();
        const data = ev.dataTransfer.getData("type");
        this.addNodeToDrawFlow(data, ev.clientX, ev.clientY);
    }

    addNodeToDrawFlow(name, pos_x, pos_y) {
        const editor = this.editor;
        if(editor.editor_mode === 'fixed') {
            return false;
        }

        pos_x = pos_x * ( editor.precanvas.clientWidth / (editor.precanvas.clientWidth * editor.zoom)) - (editor.precanvas.getBoundingClientRect().x * ( editor.precanvas.clientWidth / (editor.precanvas.clientWidth * editor.zoom)));
        pos_y = pos_y * ( editor.precanvas.clientHeight / (editor.precanvas.clientHeight * editor.zoom)) - (editor.precanvas.getBoundingClientRect().y * ( editor.precanvas.clientHeight / (editor.precanvas.clientHeight * editor.zoom)));

        switch (name) {
            case 'trigger':
                return editor.addNode('trigger', 0, 1, pos_x, pos_y, 'trigger', {}, this.getNodeContent("trigger-node"));
            case 'send-email': {
                const nodeId = editor.addNode('send-email', 1, 1, pos_x, pos_y, 'send-email', {}, this.getNodeContent("send-email-node"));
                this.initSendEmailNode(nodeId);
                return nodeId;
            }
            case 'send-sms':
                return editor.addNode('send-sms', 1, 1, pos_x, pos_y, 'send-sms', {}, this.getNodeContent("send-sms-node"));
            case 'tag-untag':
                return editor.addNode('tag-untag', 1, 1, pos_x, pos_y, 'tag-untag', {}, this.getNodeContent("tag-untag-node"));
            case 'unsubscribe-guest':
                return editor.addNode('unsubscribe-guest', 1, 1, pos_x, pos_y, 'unsubscribe-guest', {}, this.getNodeContent("unsubscribe-guest-node"));
            case 'archive-guest':
                return editor.addNode('archive-guest', 1, 1, pos_x, pos_y, 'archive-guest', {}, this.getNodeContent("archive-guest-node"));
            case 'condition': {
                const nodeId = editor.addNode('condition', 1, 2, pos_x, pos_y, 'condition', {}, this.getNodeContent("condition-node"));
                this.initConditionNode(nodeId);
                return nodeId;
            }
            case 'time-delay':
                return editor.addNode('time-delay', 1, 1, pos_x, pos_y, 'time-delay', {}, this.getNodeContent("time-delay-node"));
            case 'wait-for-trigger': {
                const nodeId = editor.addNode('wait-for-trigger', 1, 2, pos_x, pos_y, 'wait-for-trigger', {}, this.getNodeContent("wait-for-trigger-node"));
                this.initWaitForTriggerNode(nodeId);
                return nodeId;
            }
            case 'end':
                return editor.addNode('end', 1, 0, pos_x, pos_y, 'end', {}, this.getNodeContent("end-node"));
            default:
        }
    }

    getNodeContent(className) {
        const template = document.getElementById('flow-node-templates');
        const scope = template?.content || document;
        return scope.querySelector("." + className).outerHTML;
    }

    refreshConnections() {
        requestAnimationFrame(() => {
            const nodes = this.editor.drawflow.drawflow.Home.data;
            Object.keys(nodes).forEach(id => {
                this.editor.updateConnectionNodes(`node-${id}`);
            });
        });

        setTimeout(() => {
            this.refreshConnections();
        }, 10);
    }

    setupConnectionClickHandler() {
        this.editor.precanvas.addEventListener('click', (e) => {
            if (e.target.classList.contains('main-path')) {
                e.stopPropagation();
                this.showConnectionDeleteBtn(e.target);
            } else if (!e.target.closest('#connection-delete-btn')) {
                this.hideConnectionDeleteBtn();
            }
        });
    }

    showConnectionDeleteBtn(pathEl) {
        this.hideConnectionDeleteBtn();

        const svg = pathEl.parentElement;
        const classes = [...svg.classList];

        const inClass = classes.find(c => c.startsWith('node_in_node-'));
        const outClass = classes.find(c => c.startsWith('node_out_node-'));
        if (!inClass || !outClass) return;

        const inputId = parseInt(inClass.replace('node_in_node-', ''));
        const outputId = parseInt(outClass.replace('node_out_node-', ''));
        const outputPort = classes.find(c => c.startsWith('output_')) || 'output_1';
        const inputPort = classes.find(c => c.startsWith('input_')) || 'input_1';

        const midPoint = pathEl.getPointAtLength(pathEl.getTotalLength() / 2);

        const btn = document.createElement('div');
        btn.id = 'connection-delete-btn';
        btn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
        Object.assign(btn.style, {
            position: 'absolute',
            left: `${midPoint.x - 11}px`,
            top: `${midPoint.y - 11}px`,
            width: '22px',
            height: '22px',
            background: '#ef4444',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: '9999',
            boxShadow: '0 2px 8px rgba(0,0,0,0.25)'
        });

        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.editor.removeSingleConnection(outputId, inputId, outputPort, inputPort);
            this.hideConnectionDeleteBtn();
        });

        this.editor.precanvas.appendChild(btn);
    }

    hideConnectionDeleteBtn() {
        const btn = document.getElementById('connection-delete-btn');
        if (btn) btn.remove();
    }

    showActionMenu(e) {
        e.preventDefault();
        e.stopPropagation();
        const menu = document.querySelector("#actions-menu");
        if (!menu) return;

        this.selectedNode = e.currentTarget.closest("[id^='node-']");

        const rect = e.currentTarget.getBoundingClientRect();
        Object.assign(menu.style, {
            zIndex: "9999",
            display: "block",
            position: "fixed",
            top: `${rect.top + 30}px`,
            left: `${rect.right - 65}px`
        });
    }

    hideActionMenu = (e) => {
        const menu = document.querySelector("#actions-menu");
        if (!menu.contains(e.target) || menu) {
            menu.style.display = "none";
        }
    };

    editNode(e) {
        const node = this.selectedNode;
        if (!Boolean(node || this.editor)) return;

        this.storeNodeSnapshot(node);
        this.showEditActions();
        this.hideActionMenu(e);
    }

    showEditActions () {
        const node = this.selectedNode;
        if (node.querySelector(".node-edit-actions")) return;

        const wrapper = document.createElement('div');
        wrapper.innerHTML = `<div class="node-edit-actions">
      <button type="button" class="node-edit-btn node-save-btn" data-action="click->mission-control--draw-flow#saveAction">Save</button>
      <button type="button" class="node-edit-btn node-discard-btn" data-action="click->mission-control--draw-flow#discardAction">Discard</button>
    </div>`

        node.classList.add("node-editing");
        node.querySelector(".node-content").appendChild(wrapper);
    }

    storeNodeSnapshot(node) {
        const snapshot = [];
        node.querySelectorAll("select, input").forEach((el) => {
            snapshot.push({ el, value: el.value });
        });
        node.editSnapshot = snapshot;
    }

    finishEditing() {
        const node = this.selectedNode
        delete node.editSnapshot;

        node.classList.remove("node-editing");

        const actions = node.querySelector(".node-edit-actions");
        if (actions) actions.remove();
    }

    toggleFilterValue(e) {
        const select = e.currentTarget;
        const input = select.closest('.delay-input-group').querySelector('.filter-value');
        if (!input) return;

        const hideInput = ["has any value", "is unknown"].includes(select.value);
        input.style.display = hideInput ? "none" : "";
    }

    saveAction(e) {
        e.stopPropagation();
        this.finishEditing();
    }

    discardAction(e) {
        e.stopPropagation();
        this.selectedNode.editSnapshot.forEach(({ el, value }) => { el.value = value; });
        this.finishEditing();
    }

    editCondition(e) {
        e.preventDefault();
        const node = e.currentTarget.closest("[id^='node-']");
        if (node) {
            this.selectedNode = node;
            this.editNode(e);
        }
    }

    initConditionNode(nodeId) {
        requestAnimationFrame(() => {
            const domNode = document.getElementById(`node-${nodeId}`);
            if (!domNode) return;
            const fieldSelect = domNode.querySelector('.condition-field-select');
            if (fieldSelect) {
                this.onConditionFieldChange({ currentTarget: fieldSelect });
            }
        });
    }

    initWaitForTriggerNode(nodeId) {
        requestAnimationFrame(() => {
            const domNode = document.getElementById(`node-${nodeId}`);
            if (!domNode) return;
            const fieldSelect = domNode.querySelector('.wft-field-select');
            if (fieldSelect) {
                this.onWaitForTriggerFieldChange({ currentTarget: fieldSelect });
            }
        });
    }

    onWaitForTriggerFieldChange(e) {
        const fieldSelect = e.currentTarget;
        const nodeBody = fieldSelect.closest('.node-body');
        if (!nodeBody) return;

        const selectedOption = fieldSelect.options[fieldSelect.selectedIndex];
        const filterType = selectedOption?.getAttribute('data-filter-type') || '';

        const operatorSelect = nodeBody.querySelector('.wft-operator-select');
        if (!operatorSelect) return;

        operatorSelect.style.display = filterType === 'location' ? '' : 'none';
    }

    onConditionFieldChange(e) {
        const fieldSelect = e.currentTarget;
        const nodeContent = fieldSelect.closest('.node-content');
        if (!nodeContent) return;
        const builder = nodeContent.querySelector('.condition-row');
        if (!builder) return;

        const selectedOption = fieldSelect.options[fieldSelect.selectedIndex];
        const filterType = selectedOption?.getAttribute('data-filter-type') || '';

        const operatorSelect = builder.querySelector('.condition-operator-select');
        const valueInput = builder.querySelector('.condition-value-input');
        const dateRange = builder.querySelector('.condition-date-range');

        fieldSelect.classList.remove('validetta-error');

        if (!fieldSelect.value || !filterType) {
            if (operatorSelect) operatorSelect.style.display = 'none';
            if (valueInput) valueInput.style.display = 'none';
            if (dateRange) dateRange.style.display = 'none';
            if (operatorSelect) operatorSelect.onchange = null;
            return;
        }

        // Duration-range replaces the operator+value pair with a Start/End
        // datetime pair. There's no operator concept, so hide the operator
        // select entirely and let the dates carry the filter.
        if (filterType === 'duration-range') {
            operatorSelect.style.display = 'none';
            valueInput.style.display = 'none';
            if (dateRange) dateRange.style.display = 'flex';
            operatorSelect.onchange = null;
            return;
        }

        if (dateRange) dateRange.style.display = 'none';

        // Show/hide operator options by filter type class
        const allOptions = operatorSelect.querySelectorAll('option');
        allOptions.forEach(opt => {
            opt.style.display = opt.classList.contains('op-' + filterType) ? '' : 'none';
            opt.selected = false;
        });

        // Select the first visible option
        const firstVisible = operatorSelect.querySelector('option[style=""], option:not([style])');
        if (firstVisible) firstVisible.selected = true;

        operatorSelect.style.display = filterType ? '' : 'none';

        // Show value input only for types that need a free-text/number value
        const needsValue = ['string', 'integer', 'date', 'birthday', 'engagement-integer', 'trigger'];
        const noValueOperators = ['is unknown', 'has any value', 'has occurred'];
        const currentOp = operatorSelect.value;

        if (needsValue.includes(filterType) && !noValueOperators.includes(currentOp)) {
            valueInput.style.display = '';
            if (['integer', 'engagement-integer'].includes(filterType)) {
                valueInput.type = 'number';
                valueInput.placeholder = 'Value…';
            } else if (['date', 'birthday'].includes(filterType)) {
                valueInput.type = 'number';
                valueInput.placeholder = 'Days…';
            } else if (filterType === 'trigger') {
                // Operator label drives the unit (times / days / hours);
                // input is always numeric.
                valueInput.type = 'number';
                valueInput.placeholder = triggerValuePlaceholder(currentOp);
            } else {
                valueInput.type = 'text';
                valueInput.placeholder = 'Value…';
            }
        } else {
            valueInput.style.display = 'none';
        }

        // Listen for operator changes to toggle the value input
        operatorSelect.onchange = () => {
            const op = operatorSelect.value;
            if (needsValue.includes(filterType) && !noValueOperators.includes(op)) {
                valueInput.style.display = '';
            } else {
                valueInput.style.display = 'none';
            }
        };
    }

    initSendEmailNode(nodeId) {
        requestAnimationFrame(() => {
            const domNode = document.getElementById(`node-${nodeId}`);
            if (!domNode) return;

            const select = domNode.querySelector('#from-email-select');
            if (select) {
                this.onEmailFromSelectChange({ currentTarget: select });
            }
        });
    }

    refreshSenderEmailOptions() {
        requestAnimationFrame(() => {
            const template = document.getElementById('flow-node-templates');
            const scope = template?.content || document;
            const freshSelect = scope.querySelector('.send-email-node #from-email-select');
            if (!freshSelect) return;

            const nodes = this.editor?.drawflow?.drawflow?.Home?.data;
            if (!nodes) return;

            Object.keys(nodes).forEach(id => {
                if (nodes[id]?.name !== 'send-email') return;

                const nodeEl = document.getElementById(`node-${id}`);
                const select = nodeEl?.querySelector('#from-email-select');
                const nodeContent = select?.closest('.node-content');
                if (!select || !nodeContent) return;

                const previousValue = select.value;
                select.innerHTML = freshSelect.innerHTML;

                if (select.querySelector(`option[value="${CSS.escape(previousValue)}"]`)) {
                    select.value = previousValue;
                }

                if (select.value === '__add_new__') {
                    const customInput = nodeContent.querySelector('#email-from-email');
                    this.validateFromEmailVerification(nodeContent, customInput?.value || '');
                } else {
                    const selectedOption = select.options[select.selectedIndex];
                    this.toggleFromEmailError(nodeContent, selectedOption?.dataset?.verified !== 'true');
                }
            });

            this.updateSequenceJson();
        });
    }

    onEmailFromSelectChange(e) {
        const select = e.currentTarget;
        const nodeContent = select.closest('.node-content');
        const input = nodeContent?.querySelector('#email-from-email');
        if (!nodeContent || !input) return;

        const isAddNew = select.value === '__add_new__';
        input.classList.toggle('hidden', !isAddNew);

        if (isAddNew) {
            input.value = '';
            input.focus();
            this.toggleFromEmailError(nodeContent, false); // Clear error on initial empty custom text
        } else {
            input.value = select.value;
            const selectedOption = select.options[select.selectedIndex];
            const isVerified = selectedOption?.dataset?.verified === 'true';
            this.toggleFromEmailError(nodeContent, !isVerified);
        }
    }

    onEmailFromInputChange(e) {
        const input = e.currentTarget;
        const nodeContent = input.closest('.node-content');
        if (!nodeContent) return;

        this.validateFromEmailVerification(nodeContent, input.value);
    }

    validateFromEmailVerification(nodeContent, rawValue) {
        const value = rawValue?.trim().toLowerCase();
        if (!value) {
            this.toggleFromEmailError(nodeContent, false);
            return;
        }

        const select = nodeContent.querySelector('#from-email-select');
        if (!select) return;

        // Use querySelector instead of converting the whole list to an array and running .some()
        const verifiedMatch = !!select.querySelector(`option[data-verified="true"][value="${CSS.escape(value)}"]`);
        this.toggleFromEmailError(nodeContent, !verifiedMatch);
    }

    toggleFromEmailError(nodeContent, show) {
        nodeContent?.querySelector('.email-from-error')?.classList.toggle('hidden', !show);
    }

    async saveAsDraftAndGoToSenderEmails(e) {
        e.preventDefault();
        const link = e.currentTarget;
        if (link.classList.contains('saving')) return;

        const url = link.getAttribute('href');
        link.classList.add('saving');
        link.setAttribute('aria-disabled', 'true');

        this.performAutosave().then(() => {
            toastr.success('Saved your campaign as a draft.');
        }).catch(err => {
            console.error('saveAsDraftAndGoToSenderEmails failed', err);
            toastr.error('Could not save your campaign as a draft. Please try again.');
        }).finally(() => {
            window.location.href = url;
            link.classList.remove('saving');
            link.removeAttribute('aria-disabled');
        });
    }

    // === Trigger Filter Modal ===

    openTriggerFilterModal(e) {
        if (e) e.stopPropagation();
        const node = e?.currentTarget?.closest("[id^='node-']");
        if (!node) return;
        this.triggerEditingNode = node;

        const modal = document.getElementById('trigger-modal');
        if (!modal) return;

        // Remove every row except the first — the first row is baked into the
        // markup and acts as the primary editor (and as the clone source).
        const rows = modal.querySelectorAll('.trigger-row');
        rows.forEach((row, i) => { if (i > 0) row.remove(); });

        const firstRow = modal.querySelector('.trigger-row');
        if (firstRow) this.resetTriggerRow(firstRow);

        // Rehydrate saved filters from chip inputs on the node
        const chips = node.querySelectorAll('.trigger-chip-input');
        const filters = [...chips].map(chip => ({
            search_field: chip.querySelector('[data-key="search_field"]')?.value || '',
            query_type:   chip.querySelector('[data-key="query_type"]')?.value || '',
            filter_type:  chip.querySelector('[data-key="filter_type"]')?.value || '',
            search_value: chip.querySelector('[data-key="search_value"]')?.value || '',
            start_date:   chip.querySelector('[data-key="start_date"]')?.value || '',
            end_date:     chip.querySelector('[data-key="end_date"]')?.value || '',
        }));

        if (filters.length > 0 && firstRow) {
            this.applyFilterDataToRow(firstRow, filters[0]);
            for (let i = 1; i < filters.length; i++) {
                this.addTriggerFilterRow(filters[i]);
            }
        }

        this.loadTriggerEventIntoModal(node);
        this.updateTriggerAddButton();
        this.updateTriggerRemoveButtons();
        modal.style.display = 'flex';
    }

    loadTriggerEventIntoModal(node) {
        const modal = document.getElementById('trigger-modal');
        const select = modal?.querySelector('.trigger-event-select');
        if (!select) return;
        const savedEvent = node?.querySelector('.trigger-event-hidden')?.value;
        if (savedEvent && [...select.options].some(o => o.value === savedEvent)) {
            select.value = savedEvent;
        }
    }

    resetTriggerRow(row) {
        if (!row) return;
        row.querySelectorAll('input').forEach(input => {
            if (input.type === 'checkbox' || input.type === 'radio') {
                input.checked = false;
            } else if (input.type === 'datetime-local') {
                // Keep the template's default value (1.day.ago / Time.current)
                // — clearing would leave the duration-range UI blank when the
                // user picks it for the first time.
                input.value = input.getAttribute('value') || input.value;
            } else {
                input.value = '';
                // Hide the free-text value input; date inputs stay visible
                // when the row is in the duration-range mode.
                input.style.display = 'none';
            }
        });
        row.querySelectorAll('select').forEach(select => {
            select.selectedIndex = 0;
        });

        // After resetting, scope the operator dropdown to the default filter
        // field so it doesn't show every operator across all filter types.
        const fieldSelect = row.querySelector('.trigger-field');
        const defaultOpt = fieldSelect?.options[fieldSelect.selectedIndex];
        const filterType = defaultOpt?.getAttribute('data-filter-type') || '';
        this.applyTriggerFilterTypeToRow(row, filterType, { autoSelectFirst: true });
    }

    applyFilterDataToRow(row, data) {
        if (!row || !data) return;
        const fieldSelect = row.querySelector('.trigger-field');

        // Handle both old format (field) and new format (search_field)
        const fieldValue = data.search_field || data.field;
        if (fieldSelect && fieldValue != null) fieldSelect.value = fieldValue;

        // Restrict the operator dropdown to options that match the saved filter
        // type before applying the saved operator value.
        const fieldOpt = fieldSelect?.options[fieldSelect.selectedIndex];
        const filterType = data.filter_type || fieldOpt?.getAttribute('data-filter-type') || '';
        this.applyTriggerFilterTypeToRow(row, filterType);

        // Duration-range carries date inputs instead of an operator/value pair.
        // Push the saved start/end into the row's date controls so the modal
        // re-renders the user's previous selection on re-open.
        if (filterType === 'duration-range') {
            const startInput = row.querySelector('.trigger-start-date');
            const endInput   = row.querySelector('.trigger-end-date');
            if (startInput && data.start_date) startInput.value = data.start_date;
            if (endInput   && data.end_date)   endInput.value   = data.end_date;

            const filterTypeInput = row.querySelector('.campaign-filter-type');
            if (filterTypeInput && data.filter_type != null) filterTypeInput.value = data.filter_type;
            return;
        }

        const opSelect = row.querySelector('.trigger-operator');
        // Handle both old format (operator) and new format (query_type)
        const opValue = data.query_type || data.operator;
        if (opSelect && opValue != null) opSelect.value = opValue;

        const valInput = row.querySelector('.trigger-value');
        // Handle both old format (value) and new format (search_value)
        const displayValue = data.search_value || data.value;
        // Filter types like gender, provider, review, is_subscribed,
        // sms_subscribed, location, and guest_tag treat the operator dropdown
        // itself as the value, so we must not surface the free-text value
        // input on edit even though `search_value` got persisted (it mirrors
        // `query_type` for these filters — see saveTriggerFilters fallback).
        // The same applies to no-value operators (is unknown / has any value
        // / has occurred) on birthday/date/trigger/etc.
        if (valInput) {
            if (triggerOperatorNeedsValueInput(filterType, opValue) && displayValue) {
                applyTriggerValueInputType(valInput, filterType, opValue);
                valInput.value = displayValue;
                valInput.style.display = '';
            } else {
                valInput.value = '';
                valInput.style.display = 'none';
            }
        }

        const filterTypeInput = row.querySelector('.campaign-filter-type');
        if (filterTypeInput && data.filter_type != null) filterTypeInput.value = data.filter_type;
    }

    // Restricts the row's operator <select> to options that match the given
    // filter type. When autoSelectFirst is true (a fresh filter-type pick by
    // the user) it also auto-selects the first matching operator and fires a
    // change event so dependent UI (e.g. the value input) updates.
    applyTriggerFilterTypeToRow(row, filterType, { autoSelectFirst = false } = {}) {
        if (!row) return;

        const filterTypeInput = row.querySelector('.campaign-filter-type');
        if (filterTypeInput) filterTypeInput.value = filterType;

        const operatorSelect = row.querySelector('.trigger-operator');
        if (!operatorSelect) return;

        const valueInput = row.querySelector('.trigger-value');
        const dateRange = row.querySelector('.trigger-date-range');

        // Duration-range owns the row by itself: a Start/End datetime pair
        // replaces the operator+value controls. Skip the operator-class
        // shuffle and just toggle visibility.
        if (filterType === 'duration-range') {
            operatorSelect.style.display = 'none';
            operatorSelect.value = '';
            operatorSelect.selectedIndex = -1;
            if (valueInput) {
                valueInput.value = '';
                valueInput.style.display = 'none';
            }
            if (dateRange) dateRange.style.display = 'flex';
            return;
        }

        if (dateRange) dateRange.style.display = 'none';

        operatorSelect.style.display = filterType ? '' : 'none';
        if (valueInput) {
            valueInput.value = '';
            valueInput.style.display = 'none';
        }

        // Single pass over the operator options: scope them to the filter
        // type, capture the first match, and (when auto-selecting) clear the
        // prior selection in the same loop. Iterating the live `.options`
        // collection once avoids two `querySelectorAll('option')` sweeps over
        // 40+ nodes on every field pick.
        const matchClass = 'op-' + filterType;
        let firstMatch = null;
        const options = operatorSelect.options;
        for (let i = 0; i < options.length; i++) {
            const option = options[i];
            const matches = option.classList.contains(matchClass);
            option.style.display = matches ? '' : 'none';
            option.disabled = !matches;
            if (matches && !firstMatch) firstMatch = option;
            if (autoSelectFirst) option.selected = false;
        }

        if (!autoSelectFirst) return;

        if (firstMatch) {
            firstMatch.selected = true;
            operatorSelect.value = firstMatch.value;
            // Update the dependent value input directly. Dispatching a
            // synthetic bubbling `change` here made every field pick walk the
            // whole DOM (incl. the canvas-level capture listener), which is
            // what made the modal feel sluggish.
            this.syncTriggerValueInput(row);
        } else {
            // No matching operator (e.g. `none` / All Guests) — clear the
            // current value so a stale operator from a previous filter
            // type doesn't get serialised on save.
            operatorSelect.value = '';
            operatorSelect.selectedIndex = -1;
        }
    }

    // Accepts either a click Event (from the Add Filter button) or a plain
    // filter-data object (from openTriggerFilterModal when rehydrating saved filters).
    addTriggerFilterRow(arg) {
        const isEvent = typeof Event !== 'undefined' && arg instanceof Event;
        const data = !isEvent && arg && typeof arg === 'object' ? arg : null;
        if (isEvent) arg.stopPropagation();

        const modal = document.getElementById('trigger-modal');
        if (!modal) return;

        const addBtn = modal.querySelector('.trigger-add-btn');
        if (!addBtn) return;

        const container = addBtn.parentNode;
        const existingRows = container.querySelectorAll('.trigger-row');
        if (existingRows.length >= 3) return;

        const templateRow = existingRows[0] || modal.querySelector('.trigger-row');
        if (!templateRow) return;

        const row = templateRow.cloneNode(true);
        container.insertBefore(row, addBtn);

        // Reset the clone so it starts blank instead of inheriting the source
        // row's values (also scopes the operator dropdown to the default field).
        this.resetTriggerRow(row);

        if (data) this.applyFilterDataToRow(row, data);

        this.updateTriggerAddButton();
        this.updateTriggerRemoveButtons();
    }

    removeTriggerFilterRow(e) {
        e.stopPropagation();
        const row = e.currentTarget.closest('.trigger-row');
        if (!row) return;

        const modal = document.getElementById('trigger-modal');
        const totalRows = modal?.querySelectorAll('.trigger-row').length || 0;

        // Always keep at least one filter row in the modal — instead of
        // removing the only remaining row, reset it back to its empty state
        // so the user can re-edit it without re-opening the modal.
        if (totalRows <= 1) {
            this.resetTriggerRow(row);
        } else {
            row.remove();
        }

        this.updateTriggerAddButton();
        this.updateTriggerRemoveButtons();
    }

    updateTriggerAddButton() {
        const modal = document.getElementById('trigger-modal');
        const count = modal.querySelectorAll('.trigger-row').length;
        const addBtn = modal.querySelector('.trigger-add-btn');
        addBtn.style.display = count > 2 ? 'none' : '';
    }

    // Hides the row's remove (×) button when only one row is left, so the UI
    // visually signals that the last filter can't be removed.
    updateTriggerRemoveButtons() {
        const modal = document.getElementById('trigger-modal');
        if (!modal) return;
        const rows = modal.querySelectorAll('.trigger-row');
        rows.forEach(row => {
            const btn = row.querySelector('.trigger-remove');
            if (btn) btn.style.visibility = rows.length <= 1 ? 'hidden' : '';
        });
    }

    saveTriggerFilters() {
        const modal = document.getElementById('trigger-modal');
        if (!modal) return;

        const rows = modal.querySelectorAll('.trigger-row');
        const filters = [];

        rows.forEach(row => {
            const fieldSelect = row.querySelector('.trigger-field');
            const fieldOpt = fieldSelect?.options[fieldSelect.selectedIndex];
            const searchField = fieldSelect?.value || '';
            const filterType = fieldOpt?.getAttribute('data-filter-type') || '';

            // Duration-range carries a start/end date pair instead of a
            // single value/operator — query_type is fixed to "between" so
            // the server-side filter has a consistent identifier.
            if (filterType === 'duration-range') {
                filters.push({
                    query_type:  'between',
                    filter_type: filterType,
                    search_field: searchField,
                    search_value: '',
                    start_date:  row.querySelector('.trigger-start-date')?.value || '',
                    end_date:    row.querySelector('.trigger-end-date')?.value || ''
                });
                return;
            }

            const queryType = row.querySelector('.trigger-operator')?.value || '';
            const searchValue = row.querySelector('.trigger-value')?.value || queryType;

            // Generate format: { query_type, filter_type, search_field, search_value }
            filters.push({
                query_type: queryType,
                filter_type: filterType,
                search_field: searchField,
                search_value: searchValue
            });
        });

        // Prefer the node that opened the modal; otherwise fall back to the
        // trigger node(s) currently on the canvas so chips still render when
        // the modal was opened via the generic `showModal` handler.
        const targetNodes = this.triggerEditingNode
            ? [this.triggerEditingNode]
            : [...document.querySelectorAll("[id^='node-'] .trigger-node")]
                .map(el => el.closest("[id^='node-']"))
                .filter(Boolean);

        const eventSelect = modal.querySelector('.trigger-event-select');
        const triggerEventValue = eventSelect?.value || '';
        const triggerEventLabel = eventSelect?.options[eventSelect.selectedIndex]?.textContent?.trim() || '';

        targetNodes.forEach(node => {
            if (triggerEventValue) {
                const eventHidden = node.querySelector('.trigger-event-hidden');
                if (eventHidden) eventHidden.value = triggerEventValue;

                const eventNameEl = node.querySelector('.trigger-event-name');
                if (eventNameEl) eventNameEl.textContent = triggerEventLabel || triggerEventValue;
            }

            const countEl = node.querySelector('.trigger-count');
            if (countEl) countEl.textContent = filters.length;

            this.renderTriggerChipInputs(node, filters);
        });

        this.fetchAndRenderRecipientCount(filters, targetNodes);

        // If at least one filter row resolved to a real search_field the
        // trigger no longer counts as empty — drop the "no filters" error
        // off any trigger nodes that just got refreshed.
        if (filters.some(f => f.search_field)) {
            targetNodes.forEach(node => this.clearNodeErrorByCode(node, 'trigger_no_filters'));
        }

        this.closeModal();
        this.updateSequenceJson();
    }

    // Inserts the recipient count badge into a trigger node when missing so
    // campaigns saved before this UI was added still get the live count once
    // the user re-applies filters.
    ensureTriggerCountEl(node) {
        const triggerNode = node.querySelector('.trigger-node') || node;
        let countEl = triggerNode.querySelector('.trigger-recipient-count');
        if (countEl) return countEl;

        const header = triggerNode.querySelector('.node-header');
        if (!header) return null;

        countEl = document.createElement('span');
        countEl.className = 'trigger-recipient-count';
        countEl.style.display = 'none';
        countEl.innerHTML = '<strong class="trigger-recipient-count-value">0</strong> <span class="trigger-recipient-count-label">guests match</span>';

        header.appendChild(countEl);
        return countEl;
    }

    // Hits the campaign preview endpoint with the modal's filters and updates
    // the trigger node's "X guests match" badge so the user can gauge the
    // audience size before saving the campaign. Failures hide the badge
    // silently after a toast — the filters themselves are still applied.
    fetchAndRenderRecipientCount(filters, targetNodes) {
        const saveBtn = document.querySelector('.trigger-save');
        const url = saveBtn?.dataset.previewCountUrl;
        if (!url || !targetNodes?.length) return;

        const populatedFilters = filters.filter(f => f.search_field);

        targetNodes.forEach(node => {
            const countEl = this.ensureTriggerCountEl(node);
            if (!countEl) return;
            const valueEl = countEl.querySelector('.trigger-recipient-count-value');

            if (!populatedFilters.length) {
                countEl.style.display = 'none';
                if (valueEl) valueEl.textContent = '0';
                return;
            }

            countEl.style.display = '';
            if (valueEl) valueEl.textContent = '…';
        });

        if (!populatedFilters.length) return;

        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || '';

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-CSRF-Token': csrfToken
            },
            credentials: 'same-origin',
            body: JSON.stringify({ trigger: populatedFilters })
        })
            .then(res => res.ok ? res.json() : Promise.reject(res))
            .then(data => {
                const count = Number(data?.count || 0);
                targetNodes.forEach(node => {
                    const valueEl = node.querySelector('.trigger-recipient-count-value');
                    const labelEl = node.querySelector('.trigger-recipient-count-label');
                    if (valueEl) valueEl.textContent = count.toLocaleString();
                    if (labelEl) labelEl.textContent = count === 1 ? 'guest matches' : 'guests match';
                });
            })
            .catch(() => {
                targetNodes.forEach(node => {
                    const countEl = node.querySelector('.trigger-recipient-count');
                    if (countEl) countEl.style.display = 'none';
                });
                if (typeof toastr !== 'undefined') {
                    toastr.error('Could not load filtered guest count.');
                }
            });
    }

    renderTriggerChipInputs(node, filters) {
        const container = node.querySelector('.trigger-chips');
        if (!container) return;
        container.innerHTML = '';
        if (!filters.length) return;

        const template = document.querySelector('.trigger-row');
        const fieldOptions = template ? [...template.querySelectorAll('.trigger-field option')] : [];
        const opOptions = template ? [...template.querySelectorAll('.trigger-operator option')] : [];

        filters.forEach(f => {
            const fieldValue = f.search_field || f.field || '';
            const opValue = f.query_type || f.operator || '';
            const filterType = f.filter_type || '';
            const displayValue = f.search_value || f.value || '';

            const fieldOpt = fieldOptions.find(o => o.value === fieldValue && (!filterType || o.getAttribute('data-filter-type') === filterType));
            const fieldLabel = fieldOpt?.textContent?.trim() || fieldValue;
            const opOpt = opOptions.find(o => o.value === opValue && (!filterType || o.classList.contains('op-' + filterType)));
            const opLabel = opOpt?.textContent?.trim() || opValue;

            const chip = document.createElement('div');
            chip.className = 'trigger-chip-input';

            // Hidden inputs carry the actual values — both for form submission
            // (nested under campaign[trigger][]) and for rehydrating the modal
            // (where we need the option value, not the human-readable label,
            // to set the <select> correctly).
            const makeHidden = (val, key) => {
                const el = document.createElement('input');
                el.type = 'hidden';
                el.name = `campaign[trigger][][${key}]`;
                el.setAttribute('data-key', key);
                el.value = val;
                return el;
            };

            chip.appendChild(makeHidden(filterType, 'filter_type'));
            chip.appendChild(makeHidden(fieldValue, 'search_field'));
            chip.appendChild(makeHidden(opValue, 'query_type'));
            chip.appendChild(makeHidden(displayValue, 'search_value'));

            // Duration-range carries an extra start/end pair that the
            // server-side `filter_by_created_at` consumes directly. Persist
            // them as additional chip inputs so they round-trip on submit
            // and on modal re-open via `openTriggerFilterModal`.
            if (filterType === 'duration-range') {
                chip.appendChild(makeHidden(f.start_date || '', 'start_date'));
                chip.appendChild(makeHidden(f.end_date   || '', 'end_date'));
            }

            // Visible labels are display-only spans so the chip text stays
            // human-readable without polluting the actual stored values.
            const makeLabel = (text, cls) => {
                const span = document.createElement('span');
                span.className = `chip-display ${cls}`;
                span.textContent = text;
                return span;
            };

            chip.appendChild(makeLabel(fieldLabel, 'chip-field-display'));
            // The All-Guests pass-through has no operator or value — render
            // just the field label so the chip reads as a clean "All Guests"
            // tag instead of "All Guests   " with stray empty spans.
            if (filterType === 'duration-range') {
                const startLabel = (f.start_date || '').replace('T', ' ');
                const endLabel   = (f.end_date   || '').replace('T', ' ');
                if (startLabel || endLabel) {
                    chip.appendChild(makeLabel(`${startLabel} – ${endLabel}`, 'chip-value-display'));
                }
            } else if (filterType !== 'none') {
                chip.appendChild(makeLabel(opLabel, 'chip-op-display'));
                // Only render the value chip when the operator actually needs
                // a separate value. Dropdown-only filter types (gender, etc.)
                // and no-value operators (is unknown / has any value /
                // has occurred) bake the value into the operator itself, so
                // surfacing search_value would just render the operator
                // label twice (e.g. "Birthday is unknown is unknown").
                if (displayValue && triggerOperatorNeedsValueInput(filterType, opValue)) {
                    chip.appendChild(makeLabel(displayValue, 'chip-value-display'));
                }
            }

            container.appendChild(chip);
        });
    }

    onTriggerFilterChange(e) {
        const fieldSelect = e.currentTarget;
        const row = fieldSelect.closest('.trigger-row');
        if (!row) return;

        const selectedOption = fieldSelect.selectedOptions[0];
        const filterType = selectedOption?.dataset.filterType || '';

        // applyTriggerFilterTypeToRow clears + hides the value input itself,
        // so there's no need to do it again up front.
        this.applyTriggerFilterTypeToRow(row, filterType, { autoSelectFirst: true });
    }

    onTriggerOperatorChange(e) {
        const row = e.currentTarget.closest('.trigger-row');
        this.syncTriggerValueInput(row);
    }

    // Shows/hides + retypes the row's free-text value input based on the
    // currently-selected filter type + operator. Called directly (rather
    // than via a dispatched `change` event) so a field pick doesn't have to
    // bubble a synthetic event through the whole DOM to update the input.
    syncTriggerValueInput(row) {
        if (!row) return;

        const operatorSelect = row.querySelector('.trigger-operator');
        const valueInput = row.querySelector('.trigger-value');
        if (!operatorSelect || !valueInput) return;

        const fieldSelect = row.querySelector('.trigger-field');
        const selectedOption = fieldSelect?.options[fieldSelect.selectedIndex];
        const filterType = selectedOption?.getAttribute('data-filter-type') || '';

        const currentOp = operatorSelect.value;
        if (triggerOperatorNeedsValueInput(filterType, currentOp)) {
            valueInput.style.display = '';
            applyTriggerValueInputType(valueInput, filterType, currentOp);
        } else {
            // No-value operators (is unknown / has any value / has occurred)
            // and dropdown-only filter types (gender, provider, review,
            // is_subscribed, sms_subscribed, location, guest_tag) carry the
            // value in the operator itself — clear+hide the free-text input
            // so a stale value can't bleed back into the saved filter.
            valueInput.value = '';
            valueInput.style.display = 'none';
        }
    }

    // === Trigger Re-entry Toggle ===

    onTriggerReentryToggle(e) {
        e.stopPropagation();
        const node = e.currentTarget.closest("[id^='node-']");
        if (!node) return;
        const cooldownDiv = node.querySelector('.trigger-cooldown');
        cooldownDiv.style.display = e.currentTarget.checked ? 'flex' : 'none';
        this.updateSequenceJson();
    }

    // === Email Design (BEE editor) ===

    openEmailDesignModal(e) {
        e.stopPropagation();
        const modal = document.getElementById('email-design-modal');
        if (!modal) return;
        const beeController = this.application.getControllerForElementAndIdentifier(
            modal, "mission-control--campaign--bee"
        );
        if (beeController) beeController.open(e);
    }

    // === Email Template Selection ===

    openEmailTemplateModal(e) {
        e.stopPropagation();
        const node = e.currentTarget.closest("[id^='node-']");
        if (!node) return;
        this.emailEditingNode = node;
        $('#email-template-modal').modal('show');
    }

    selectEmailTemplate(e) {
        e.stopPropagation();
        const card = e.currentTarget;
        const templateId = card.dataset.templateId;
        const templateName = card.dataset.templateName;
        const node = this.emailEditingNode;
        if (!node) return;

        node.querySelector('.email-template-id').value = templateId;
        const contentHidden = node.querySelector('.email-content');
        if (contentHidden) contentHidden.value = templateId;

        const preview = node.querySelector('.email-body-preview');
        preview.textContent = templateName;
        preview.classList.add('has-content');

        // Reveal the inline unselect affordance now that there's a
        // template attached. Visibility is stored in inline style so it
        // round-trips through Drawflow's `outerHTML` snapshotting and
        // reappears correctly when the campaign is reopened for edit.
        const unselectBtn = node.querySelector('.email-unselect-btn');
        if (unselectBtn) unselectBtn.style.display = '';

        $('#email-template-modal').modal('hide');
        this.updateSequenceJson();
        this.clearNodeErrorByCode(node, 'email_no_content');
        // The spam-score badge listens for this on the node element. We
        // dispatch from the node (not window) so each badge only reacts to
        // its own template change — otherwise picking a template in one
        // node would re-trigger every other node's check.
        node.dispatchEvent(new CustomEvent('spam-score:revalidate', { bubbles: false }));
        toastr.success('Template "' + templateName + '" selected.');
    }

    // Companion to `selectEmailTemplate` — wipes the template attachment
    // and any custom-design payload, snaps the preview back to its empty
    // state, and re-hides itself so a subsequent template pick or custom
    // design can take over cleanly. Mirrors the field-clearing that
    // `bee_controller.js#onSaveComplete` does when a custom design
    // overrides a previously-selected template, just in the inverse
    // direction.
    unselectEmailTemplate(e) {
        e.stopPropagation();
        const node = e.currentTarget.closest("[id^='node-']");
        if (!node) return;

        node.querySelector('.email-template-id').value = '';
        const contentHidden = node.querySelector('.email-content');
        if (contentHidden) contentHidden.value = '';

        // Also clear any Bee-editor payload — leaving stale json/html
        // around would let the next "Design" click reopen the editor
        // pre-populated with the abandoned template content, which
        // contradicts the intent of "unselect".
        const jsonHidden = node.querySelector('.email-json');
        if (jsonHidden) jsonHidden.value = '';
        const htmlHidden = node.querySelector('.email-html');
        if (htmlHidden) htmlHidden.value = '';

        const preview = node.querySelector('.email-body-preview');
        if (preview) {
            preview.textContent = 'No email designed yet';
            preview.classList.remove('has-content');
        }

        e.currentTarget.style.display = 'none';

        this.updateSequenceJson();
        // Reset the spam-score badge to its empty state; without this the
        // last validated score would linger after the user unselected the
        // template, which is misleading.
        node.dispatchEvent(new CustomEvent('spam-score:revalidate', { bubbles: false }));
    }

    // === SMS Design ===

    openSmsDesignModal(e) {
        e.stopPropagation();

        if (this.smsRestricted) {
            this.showUpgradeToProModal();
            return;
        }

        const node = e.currentTarget.closest("[id^='node-']");
        if (!node) return;

        this.smsEditingNode = node;
        const modal = document.getElementById('sms-design-modal');
        modal.style.display = 'flex';

        if (!this.smsQuill) {
            this.smsQuill = new Quill("#sms-design-editor", {
                theme: "snow",
                modules: {
                    toolbar: [["emoji"]],
                    "emoji-toolbar": true,
                },
                placeholder: "Type your SMS message here…"
            });

            // Only react to typing (`text-change`), not cursor/selection
            // moves, and coalesce bursts of keystrokes into a single update
            // per animation frame so the recompute never blocks input.
            this.smsQuill.on('text-change', () => {
                this.scheduleSmsCounterUpdate();
            });
        }

        const existingBody = node.querySelector('.sms-body').value;
        if (existingBody) {
            this.smsQuill.root.innerHTML = existingBody;
        } else {
            this.smsQuill.setText('');
        }
        this.updateSmsCounters();
        this.smsQuill.focus();
    }

    showModal(e) {
        const modal = document.getElementById(e.currentTarget.dataset.modal);
        if (!modal) return;
        modal.style.display = 'flex';
    }

    closeModal(e) {
        document.querySelectorAll('.modal').forEach(modal => {
            modal.style.display = 'none';
        });
        this.triggerEditingNode = null;
    }

    closeSmsDesignModal(e) {
        if (e) e.stopPropagation();
        const modal = document.getElementById('sms-design-modal');
        modal.style.display = 'none';
        this.smsEditingNode = null;
    }

    saveSmsDesign(e) {
        e.stopPropagation();
        const node = this.smsEditingNode;
        if (!node || !this.smsQuill) return;

        // Persist the visible text (with real Unicode emojis), not Quill's
        // HTML output. The SMS dispatcher (Twilio::DispatchCampaignSmsJob)
        // sends `template.html_body` verbatim, so storing markup would mean
        // recipients literally see "<p>Hello 😀</p>" in their messages.
        const textContent = this.smsQuill.getText().replace(/\n+$/, '');

        node.querySelector('.sms-body').value = textContent;

        // Persist the freshly-computed segment count on the node so the parser
        // can store it in CampaignTemplate#num_of_segments. We call
        // updateSmsCounters() first to guarantee the metrics reflect the final
        // editor content (in case `text-change` was debounced).
        const { segments } = this.computeSmsMetrics();
        const segmentsField = node.querySelector('.sms-segments');
        if (segmentsField) segmentsField.value = segments;

        const preview = node.querySelector('.sms-body-preview');
        if (textContent) {
            preview.textContent = textContent.length > 40 ? textContent.substring(0, 40) + '…' : textContent;
            preview.classList.add('has-content');
        } else {
            preview.textContent = 'No message yet';
            preview.classList.remove('has-content');
        }

        this.clearNodeErrorByCode(node, 'sms_no_body');
        this.closeSmsDesignModal();
        this.updateSequenceJson();
    }

    // Returns { totalLength, messages, segments } for the current Quill body
    // PLUS the account's default unsubscribe footer (which the dispatcher
    // appends at send time — see Twilio::DispatchSmsJob). We mirror that here
    // so the segment count the user sees and the count we persist match what
    // the recipient will actually be billed for.
    computeSmsMetrics() {
        if (!this.smsQuill) return { totalLength: 0, messages: 0, segments: 0 };

        const text = this.smsQuill.getText();
        const bodyLength = Math.max(text.length - 1, 0); // Quill always has a trailing \n
        const defaultText = document.getElementById('sms-design-modal')?.dataset.smsDefaultText || '';
        const totalLength = bodyLength + defaultText.length;

        const combined = text + defaultText;
        const hasUnicode = [...combined].some(c => c.charCodeAt(0) > 127);
        const segmentSize = hasUnicode ? 70 : 160;
        const segments = totalLength > 0 ? Math.ceil(totalLength / segmentSize) : 0;
        const messages = totalLength > 0 ? Math.ceil(totalLength / 160) : 0;

        return { totalLength, messages, segments };
    }

    // Coalesce rapid keystrokes into one counter refresh per frame so the
    // metric computation + DOM writes stay off the input critical path.
    scheduleSmsCounterUpdate() {
        if (this._smsCounterRaf) return;
        this._smsCounterRaf = requestAnimationFrame(() => {
            this._smsCounterRaf = null;
            this.updateSmsCounters();
        });
    }

    updateSmsCounters() {
        const { totalLength, messages, segments } = this.computeSmsMetrics();

        // Cache the counter elements once — they live for the lifetime of the
        // modal, so re-querying them on every keystroke is wasted work.
        if (!this._smsCounterEls) {
            this._smsCounterEls = {
                char: document.getElementById('sms-design-char-count'),
                msg: document.getElementById('sms-design-msg-count'),
                seg: document.getElementById('sms-design-segment-count'),
                info: document.querySelector('.sms-segment-info'),
            };
        }
        const { char: charEl, msg: msgEl, seg: segEl, info } = this._smsCounterEls;

        if (charEl) charEl.textContent = totalLength;
        if (msgEl) msgEl.textContent = messages;
        if (segEl) segEl.textContent = segments;
        if (info) info.style.color = segments > 1 ? '#ef4444' : '';
    }

    cloneNode(e) {
        if (!Boolean(this.selectedNode || this.editor)) return;

        let nodeId = this.selectedNode.id.replace("node-", "");
        const node = this.editor.getNodeFromId(nodeId);
        if (node) {
            const pos_x = (node.pos_x || 0) + 50;
            const pos_y = (node.pos_y || 0) + 50;
            this.editor.addNode(node.name, Object.keys(node.inputs).length, Object.keys(node.outputs).length, pos_x, pos_y, node.class, node.data || {}, node.html);
        }
        this.hideActionMenu(e);
    }

    deleteNode(e) {
        if (!Boolean(this.selectedNode || this.editor)) return;

        this.editor.removeNodeId(this.selectedNode.id);
        this.hideActionMenu(e);
    }

    campaignSave() {
        const btn = event.currentTarget;
        this.savingButton(btn);
        this.updateSequenceJson();
        this.syncAllTriggers();
        if (this.smsRestricted && this.hasSmsNode()) {
            this.restoreButton(btn);
            this.showUpgradeToProModal();
            return;
        }

        if (!this.validateSequence()) {
            this.restoreButton(btn);
            return;
        }

        this.setCampaignStatus('pending');
        const form = this.getCampaignForm();

        this.validateSpamScoresBeforeSubmit()
            .then(canSubmit => {
                if (canSubmit) {
                    form.submit();
                } else {
                    this.restoreButton(btn);
                }
            })
            .catch(err => {
                console.error('Spam-score pre-submit validation failed', err);
                form.submit();
            });
    }

    savingButton = (btn) => {
        btn.disabled = true;
        btn.classList.add("saving");
    };

    restoreButton = (btn) => {
        btn.disabled = false;
        btn.classList.remove("saving");
    };

    saveDraft(e) {
        e.preventDefault();
        const btn = e.currentTarget;
        this.savingButton(btn)

        this.updateSequenceJson();
        this.setCampaignStatus('draft');
        this.syncAllTriggers();

        const form = this.getCampaignForm();
        if (!form) {
            this.restoreButton(btn);
            return;
        }
        form.submit();
    }

    getCampaignForm() {
        return document.querySelector('form.validetta.campaign');
    }

    setCampaignStatus(status) {
        const field = document.getElementById('campaign-status');
        if (field) field.value = status;
    }

    setupAutoSave() {
        if (!this.editor?.precanvas) return;
        const schedule = () => this.scheduleAutosave();

        this.editor.precanvas.addEventListener('input', schedule, true);
        this.editor.precanvas.addEventListener('change', schedule, true);
    }

    scheduleAutosave() {
        if (this.autoSaveTimer) return;

        this.autoSaveTimer = setInterval(() => {
            this.performAutosave().catch(err => {
                console.error('Interval autoSave failed:', err);
            });
        }, 3000);
    }

    async performAutosave() {
        this.updateSequenceJson();
        this.syncAllTriggers();

        const form = this.getCampaignForm();
        if (!form) return;

        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content || '';

        fetch(this.getCampaignUrl(), {
            method: 'POST',
            body: new FormData(form),
            credentials: 'same-origin',
            headers: {
                'Accept': 'application/json',
                'X-CSRF-Token': csrfToken
            }
        }).then(response => {
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            return response.json(); // Return the promise to the next .then()
        }).then(data => {
            this.campaignId = data.id;
        }).catch(err => {
            console.error('autoSave failed:', err);
        })
    }

    getCampaignUrl() {
        const regex = /(\/new|\/[^\/]+\/edit)$/;
        if(this.campaignId)
            return location.pathname.replace(regex, `/${this.campaignId}/autosave`)
        else
            return location.pathname.replace(regex, `/autosave`)
    }

    validateSpamScoresBeforeSubmit() {
         const widgets = [...document.querySelectorAll('[id^="node-"] .email-spam-score')];
         if (widgets.length === 0) return Promise.resolve(true);

         // Indicate that something is happening while we wait on Postmark —
         // a multi-node campaign can take a moment.
         toastr.info('Checking spam scores…', '', { timeOut: 1500 });

         const checks = widgets.map(widget => {
             const controller = this.application.getControllerForElementAndIdentifier(
                 widget, 'mission-control--campaign--email-spam-score'
             );
             if (!controller) return Promise.resolve(null);

             return controller.checkForSubmit().then(result => ({
                 nodeId:   widget.closest('[id^="node-"]')?.id?.replace('node-', ''),
                 title:    controller.nodeTitle ? controller.nodeTitle() : 'Email',
                 severity: result?.severity,
                 score:    result?.score,
                 message:  result?.message
             }));
         });

         return Promise.all(checks).then(results => {
             return this.confirmHighSpamScores(results);
         });
     }

    // Asks the user to acknowledge nodes with a "bad" spam score. Uses
     // SweetAlert (already loaded elsewhere in this codebase) so the
     // styling matches the existing destructive-confirmation pattern.
     // Only shows the modal for "bad" severity, not "warn".
     confirmHighSpamScores(results) {
         return new Promise(resolve => {
             // Filter to only include "bad" severity results; ignore "warn" and others
             const badResults = (results || []).filter(r => r?.severity === 'bad');

             // If no "bad" severity found, allow submission
             if (badResults.length === 0) {
                 resolve(true);
                 return;
             }

             let title = "Spam Risk Detected"
             let description = `One or more emails in this campaign have a <strong>high spam score</strong> and may be
                                filtered out before reaching your guests. We recommend reviewing the content to improve
                                deliverability.<br/>Do you still want to continue?`
             try {
                 swal({
                     title: title,
                     html: description,
                     type:  'warning',
                     showCancelButton:   true,
                     confirmButtonText:  'Continue Anyway',
                     cancelButtonText:   'Review Email',
                     confirmButtonClass: 'btn btn-lg btn-warning waves-effect waves-light',
                     cancelButtonClass:  'btn btn-lg btn-transparent waves-effect waves-light m-l-10',
                     buttonsStyling:     false,
                 }).then(
                     () => resolve(true),    // user confirmed override
                     () => resolve(false)    // user cancelled — fix first
                 );
             } catch (e) {
                 // If SweetAlert isn't available for some reason, fall back to
                 // the browser's native confirm so we still gate the submit.
                 const ok = window.confirm(`${title}\n\n${description}\n\nSave Anyway?`);
                 resolve(ok);
             }
         });
     }

    // === Node Validation & Error System ===
    //
    // Every check runs every save (no short-circuiting on the first failure)
    // so users see every issue across every node in one pass. Each validator
    // builds structured error objects:
    //
    //   { category, code, title, message, locationLabel, fieldSelector, fix }
    //
    // …which drive both the canvas treatment (red border + pulsing badge +
    // field highlight) and the inline popover that explains what's broken,
    // where it lives, and how to fix it.
    validateSequence() {
        const errorsByNode = new Map();
        const pushErr = (nodeId, error) => {
            if (!error) return;
            const key = String(nodeId);
            if (!errorsByNode.has(key)) errorsByNode.set(key, []);
            errorsByNode.get(key).push(error);
        };

        const nodes = this.editor.drawflow.drawflow.Home.data;
        const trigger = Object.values(nodes).find(n => n.name === "trigger");
        if (!trigger) {
            toastr.error('Automation must start with a Trigger node.');
            this.applyNodeErrors(new Map());
            return false;
        }

        this.collectConnectionErrors(nodes).forEach(({ nodeId, error }) => pushErr(nodeId, error));
        this.collectTriggerErrors(nodes).forEach(({ nodeId, error }) => pushErr(nodeId, error));
        this.collectConditionErrors(nodes).forEach(({ nodeId, error }) => pushErr(nodeId, error));
        this.collectDuplicateEmailSubjectErrors(nodes).forEach(({ nodeId, error }) => pushErr(nodeId, error));

        Object.values(nodes).forEach(node => {
            this.collectNodeContentErrors(node).forEach(error => pushErr(node.id, error));
        });

        this.applyNodeErrors(errorsByNode);

        if (errorsByNode.size > 0) {
            this.showValidationSummaryToast(errorsByNode);
            return false;
        }
        return true;
    }

    // === Collectors ===
    // Each one returns an array of { nodeId, error } entries; never mutates
    // the canvas directly so validateSequence stays the single source of
    // truth for applying error state.

    collectConnectionErrors(nodes) {
        const errors = [];
        const trigger = Object.values(nodes).find(n => n.name === 'trigger');
        if (!trigger) return errors;

        const visited = new Set();
        const stack = [trigger.id];

        while (stack.length > 0) {
            const nodeId = stack.pop();
            if (visited.has(nodeId)) continue;
            visited.add(nodeId);

            const node = nodes[nodeId];
            if (!node || node.name === 'end') continue;

            const outputs = node.outputs || {};
            const outputKeys = Object.keys(outputs);

            if (outputKeys.length === 0) {
                errors.push({
                    nodeId,
                    error: NodeError.invalid_connection({
                        code: 'no_outputs',
                        title: 'Step has no continuation',
                        message: `This ${this.humanNodeName(node.name)} step has nothing connected after it. Drag a connection down to a next step or to an End node.`,
                        locationLabel: 'Bottom output socket'
                    })
                });
                continue;
            }

            outputKeys.forEach(key => {
                const connections = outputs[key].connections || [];
                if (connections.length === 0) {
                    errors.push({
                        nodeId,
                        error: NodeError.invalid_connection({
                            code: `unconnected_${key}`,
                            title: `${this.describeMissingOutput(node, key)} is unconnected`,
                            message: 'This branch has no next step — guests reaching it will silently drop out of the campaign. Connect it to another node or to an End node.',
                            locationLabel: this.describeMissingOutput(node, key)
                        })
                    });
                }
                connections.forEach(conn => stack.push(conn.node));
            });
        }

        return errors;
    }

    collectTriggerErrors(nodes) {
        const errors = [];
        const trigger = Object.values(nodes).find(n => n.name === 'trigger');
        if (!trigger) return errors;

        const dom = document.getElementById(`node-${trigger.id}`);
        const chips = dom ? [...dom.querySelectorAll('.trigger-chip-input')] : [];
        const populated = chips.filter(chip => chip.querySelector('[data-key="search_field"]')?.value);

        if (populated.length === 0) {
            errors.push({
                nodeId: trigger.id,
                error: NodeError.missing_data({
                    code: 'trigger_no_filters',
                    title: 'No audience filters defined',
                    message: 'Pick at least one filter so the campaign knows which guests should enter this automation. Without filters nobody is enrolled.',
                    locationLabel: '"Edit Trigger" button',
                    fieldSelector: '.trigger-btn',
                    fix: 'Click "Edit Trigger" and add at least one filter row.'
                })
            });
        }

        return errors;
    }

    collectConditionErrors(nodes) {
        const errors = [];

        Object.values(nodes).forEach(node => {
            if (node?.name !== 'condition') return;

            const dom = document.getElementById(`node-${node.id}`);
            const fieldSelect = dom?.querySelector('.condition-field-select');
            if (!fieldSelect) return;

            if (!fieldSelect.value) {
                errors.push({
                    nodeId: node.id,
                    error: NodeError.configuration({
                        code: 'condition_no_field',
                        title: 'Condition field not selected',
                        message: 'Pick the guest attribute (e.g. Email, Last Visit, Loyalty Score) this condition should evaluate. Without it the Yes/No branch can\'t resolve.',
                        locationLabel: 'Condition dropdown',
                        fieldSelector: '.condition-field-select',
                        fix: 'Open the Condition dropdown and choose a field.'
                    })
                });
                return;
            }

            const opSel = dom.querySelector('.condition-operator-select');
            const valInp = dom.querySelector('.condition-value-input');
            const opt = fieldSelect.options[fieldSelect.selectedIndex];
            const filterType = opt?.getAttribute('data-filter-type') || '';
            const needsValue = ['string', 'integer', 'date', 'birthday', 'engagement-integer', 'trigger'];
            const noValueOps = ['is unknown', 'has any value', 'has occurred'];

            if (filterType === 'duration-range') {
                const start = dom.querySelector('.condition-start-date')?.value;
                const end = dom.querySelector('.condition-end-date')?.value;
                if (!start || !end) {
                    errors.push({
                        nodeId: node.id,
                        error: NodeError.missing_data({
                            code: 'condition_no_date_range',
                            title: 'Date range incomplete',
                            message: 'Both a start date and an end date are required for the Custom Duration filter.',
                            locationLabel: 'Start / End date inputs',
                            fieldSelector: '.condition-start-date'
                        })
                    });
                }
            } else if (needsValue.includes(filterType) && !noValueOps.includes(opSel?.value) && valInp && !valInp.value.trim()) {
                errors.push({
                    nodeId: node.id,
                    error: NodeError.missing_data({
                        code: 'condition_no_value',
                        title: 'Comparison value is empty',
                        message: 'Enter the value this condition should compare against — without it the branch can\'t decide between Yes and No.',
                        locationLabel: 'Comparison value input',
                        fieldSelector: '.condition-value-input'
                    })
                });
            }
        });

        return errors;
    }

    // Cross-node check: two Send Email steps that share the same subject
    // line are almost always a copy/paste mistake, and they make the live
    // report ambiguous (engagement is bucketed per CampaignTemplate, keyed
    // off the subject) and hurt inbox threading. We flag every node in a
    // duplicate group — comparison is case-insensitive and trims whitespace
    // so "Welcome" and " welcome " collide. Empty subjects are skipped here
    // because `emailErrors` already raises `email_no_subject` for those.
    collectDuplicateEmailSubjectErrors(nodes) {
        const errors = [];
        const nodesBySubject = new Map();

        Object.values(nodes).forEach(node => {
            if (node?.name !== 'send-email') return;

            const dom = document.getElementById(`node-${node.id}`);
            const subject = dom?.querySelector('#email-subject')?.value?.trim();
            if (!subject) return;

            const key = subject.toLowerCase();
            if (!nodesBySubject.has(key)) nodesBySubject.set(key, []);
            nodesBySubject.get(key).push({ nodeId: node.id, subject });
        });

        nodesBySubject.forEach(group => {
            if (group.length < 2) return;

            group.forEach(({ nodeId, subject }) => {
                errors.push({
                    nodeId,
                    error: NodeError.configuration({
                        code: 'email_duplicate_subject',
                        title: 'Duplicate email subject',
                        message: `Another Send Email step already uses the subject "${subject}". Give each email a unique subject so guests don't receive identical-looking messages and your live report can tell them apart.`,
                        locationLabel: 'Subject field',
                        fieldSelector: '#email-subject',
                        fix: 'Edit this subject line so it is different from every other email in the campaign.'
                    })
                });
            });
        });

        return errors;
    }

    collectNodeContentErrors(node) {
        switch (node.name) {
            case 'send-email':       return this.emailErrors(node);
            case 'send-sms':         return this.smsErrors(node);
            case 'time-delay':       return this.timeDelayErrors(node);
            case 'tag-untag':        return this.tagUntagErrors(node);
            case 'wait-for-trigger': return this.waitForTriggerErrors(node);
            default:                 return [];
        }
    }

    emailErrors(node) {
        const errors = [];
        const dom = document.getElementById(`node-${node.id}`);
        if (!dom) return errors;

        const subject = dom.querySelector('#email-subject')?.value?.trim();
        const fromName = dom.querySelector('#email-from-name')?.value?.trim();
        const templateId = dom.querySelector('.email-template-id')?.value;
        const json = dom.querySelector('.email-json')?.value;
        const html = dom.querySelector('.email-html')?.value;

        if (!subject) {
            errors.push(NodeError.missing_data({
                code: 'email_no_subject',
                title: 'Email subject is empty',
                message: 'Without a subject line, recipients see a blank preview in their inbox — many providers will treat it as spam and won\'t deliver it.',
                locationLabel: 'Subject field',
                fieldSelector: '#email-subject',
                fix: 'Add a short, descriptive subject line.'
            }));
        }

        if (!fromName) {
            errors.push(NodeError.missing_data({
                code: 'email_no_from_name',
                title: '"From" name is empty',
                message: 'Recipients see the from-name in their inbox preview — a blank name looks like spam.',
                locationLabel: 'From-name field',
                fieldSelector: '#email-from-name'
            }));
        }

        if (!templateId && !json && !html) {
            errors.push(NodeError.missing_data({
                code: 'email_no_content',
                title: 'No email content selected',
                message: 'Pick a template or open the Design editor — this email currently has nothing to send.',
                locationLabel: 'Template / Design buttons',
                fieldSelector: '.email-template-btn',
                fix: 'Click "Template" to pick one or "Design" to build a custom layout.'
            }));
        }

        const senderSelect = dom.querySelector('#from-email-select');
        if (senderSelect) {
            const opt = senderSelect.options[senderSelect.selectedIndex];
            const customInput = dom.querySelector('#email-from-email');
            const isCustom = senderSelect.value === '__add_new__';
            const customValue = customInput?.value?.trim();

            if (isCustom && customValue) {
                const verifiedMatch = senderSelect.querySelector(
                    `option[data-verified="true"][value="${CSS.escape(customValue.toLowerCase())}"]`
                );
                if (!verifiedMatch) {
                    errors.push(NodeError.external_service({
                        code: 'sender_not_verified',
                        title: 'Sender email isn\'t verified yet',
                        message: `"${customValue}" hasn't been verified with our email provider (AWS SES / Postmark). Campaigns can't actually send from an unverified sender.`,
                        locationLabel: 'From-email field',
                        fieldSelector: '#email-from-email',
                        fix: 'Save as draft, then verify the address on the Sender Emails settings page.'
                    }));
                }
            } else if (!isCustom && opt && opt.dataset?.verified === 'false') {
                errors.push(NodeError.external_service({
                    code: 'sender_not_verified',
                    title: 'Sender email isn\'t verified yet',
                    message: `"${senderSelect.value}" hasn't been verified with our email provider (AWS SES / Postmark). Campaigns can't actually send from an unverified sender.`,
                    locationLabel: 'From-email dropdown',
                    fieldSelector: '#from-email-select',
                    fix: 'Save as draft, then verify the address on the Sender Emails settings page.'
                }));
            }
        }

        return errors;
    }

    smsErrors(node) {
        const errors = [];
        const dom = document.getElementById(`node-${node.id}`);
        if (!dom) return errors;

        const body = dom.querySelector('.sms-body')?.value || '';
        const text = body.replace(/<[^>]*>/g, '').trim();

        if (!text) {
            errors.push(NodeError.missing_data({
                code: 'sms_no_body',
                title: 'SMS body is empty',
                message: 'Open the Design editor and write the SMS recipients will receive. An empty SMS can\'t be sent.',
                locationLabel: 'SMS message body',
                fieldSelector: '.sms-design-btn',
                fix: 'Click "Design" and type your message.'
            }));
        }

        return errors;
    }

    timeDelayErrors(node) {
        const errors = [];
        const dom = document.getElementById(`node-${node.id}`);
        if (!dom) return errors;

        const value = parseInt(dom.querySelector('.delay-value')?.value, 10);
        if (!value || value < 1) {
            errors.push(NodeError.configuration({
                code: 'delay_invalid_value',
                title: 'Delay must be at least 1',
                message: 'Set how long this step should wait before continuing. A value of zero or negative numbers would skip the delay entirely.',
                locationLabel: 'Wait value input',
                fieldSelector: '.delay-value'
            }));
        }

        return errors;
    }

    tagUntagErrors(node) {
        const errors = [];
        const dom = document.getElementById(`node-${node.id}`);
        if (!dom) return errors;

        const name = dom.querySelector('.tag-untag-field')?.value?.trim();
        if (!name) {
            errors.push(NodeError.missing_data({
                code: 'tag_untag_no_name',
                title: 'Tag name is required',
                message: 'Enter the tag name to add or remove from guests as they pass through this step.',
                locationLabel: 'Tag name input',
                fieldSelector: '.tag-untag-field'
            }));
        }

        return errors;
    }

    waitForTriggerErrors(node) {
        const errors = [];
        const dom = document.getElementById(`node-${node.id}`);
        if (!dom) return errors;

        const timeout = parseInt(dom.querySelector('.wft-timeout-value')?.value, 10);
        if (!timeout || timeout < 1) {
            errors.push(NodeError.configuration({
                code: 'wft_invalid_timeout',
                title: 'Timeout must be at least 1',
                message: 'Pick a positive timeout so the workflow knows how long to wait for the trigger event before giving up.',
                locationLabel: 'Timeout input',
                fieldSelector: '.wft-timeout-value'
            }));
        }

        return errors;
    }

    humanNodeName(name) {
        return ({
            'trigger': 'Trigger',
            'send-email': 'Send Email',
            'send-sms': 'Send SMS',
            'time-delay': 'Time Delay',
            'condition': 'Condition',
            'tag-untag': 'Tag / Untag',
            'unsubscribe-guest': 'Unsubscribe Guest',
            'archive-guest': 'Archive Guest',
            'wait-for-trigger': 'Wait for Trigger',
            'end': 'End'
        })[name] || name;
    }

    describeMissingOutput(node, outputKey) {
        if (node.name === 'condition') {
            const branch = outputKey === 'output_1' ? 'Yes' : 'No';
            return `Condition "${branch}" branch`;
        }
        if (node.name === 'wait-for-trigger') {
            const branch = outputKey === 'output_1' ? 'Action Taken' : 'Timed Out';
            return `Wait for Trigger "${branch}" branch`;
        }
        return `${this.humanNodeName(node.name)} output`;
    }

    // === Visual rendering ===

    applyNodeErrors(errorsByNode) {
        this.nodeErrors = errorsByNode;
        const nodes = this.editor.drawflow.drawflow.Home.data;

        Object.keys(nodes).forEach(id => {
            const errors = errorsByNode.get(String(id)) || [];
            this.applyNodeErrorVisuals(id, errors);
        });
        this.hideErrorPopover();
    }

    applyNodeErrorVisuals(nodeId, errors) {
        const wrapper = document.getElementById(`node-${nodeId}`);
        if (!wrapper) return;
        const flowNode = wrapper.querySelector('.flow-node, .end-node');

        // Strip any stale field highlights before reapplying
        wrapper.querySelectorAll('.node-field-error').forEach(el => el.classList.remove('node-field-error'));

        const accent = wrapper.querySelector('.node-accent');
        const originalIcon = wrapper.querySelector('.node-icon .original');
        const errorIcon = wrapper.querySelector('.node-icon .error');

        if (!errors || errors.length === 0) {
            flowNode?.classList.remove('node-has-error');
            accent?.classList.remove('accent-red');
            if (originalIcon) originalIcon.style.display = 'block';
            if (errorIcon) errorIcon.style.display = 'none';
            wrapper.querySelector('.node-error-indicator')?.remove();
            return;
        }

        flowNode?.classList.add('node-has-error');
        accent?.classList.add('accent-red');
        if (originalIcon) originalIcon.style.display = 'none';
        if (errorIcon) errorIcon.style.display = 'block';

        errors.forEach(err => {
            if (!err.fieldSelector) return;
            const target = wrapper.querySelector(err.fieldSelector);
            if (target) target.classList.add('node-field-error');
        });

        let badge = wrapper.querySelector('.node-error-indicator');
        if (!badge) {
            badge = document.createElement('button');
            badge.type = 'button';
            badge.className = 'node-error-indicator';
            badge.setAttribute('aria-label', 'View node issues');
            badge.innerHTML = `
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                    <line x1="12" y1="9" x2="12" y2="13"/>
                    <line x1="12" y1="17" x2="12.01" y2="17"/>
                </svg>
                <span class="node-error-count"></span>
            `;
            badge.addEventListener('click', (e) => {
                e.stopPropagation();
                this.toggleErrorPopover(badge, nodeId);
            });
            // Drawflow starts a drag on mousedown anywhere inside the node
            // wrapper. Swallow it so clicking the badge never accidentally
            // moves the node.
            badge.addEventListener('mousedown', (e) => e.stopPropagation());
            wrapper.appendChild(badge);
        }
        const countEl = badge.querySelector('.node-error-count');
        if (countEl) countEl.textContent = errors.length;
    }

    showValidationSummaryToast(errorsByNode) {
        const totalErrors = [...errorsByNode.values()].reduce((sum, errs) => sum + errs.length, 0);
        const nodeCount = errorsByNode.size;
        const msg = totalErrors === 1
            ? '1 issue blocks this campaign from going live. Click the warning badge on the highlighted node for details.'
            : `${totalErrors} issues across ${nodeCount} node${nodeCount === 1 ? '' : 's'} block this campaign from going live. Click the warning badges for details.`;
        toastr.error(msg, '', { timeOut: 8000, extendedTimeOut: 3000 });
    }

    toggleErrorPopover(badgeEl, nodeId) {
        if (this._errorPopover && this._errorPopover.dataset.nodeId === String(nodeId)) {
            this.hideErrorPopover();
            return;
        }
        this.showErrorPopover(badgeEl, nodeId);
    }

    showErrorPopover(badgeEl, nodeId) {
        this.hideErrorPopover();
        const errors = this.nodeErrors?.get(String(nodeId)) || [];
        if (!errors.length) return;

        const pop = document.createElement('div');
        pop.className = 'node-error-popover';
        pop.dataset.nodeId = String(nodeId);

        const itemsHtml = errors.map((err, idx) => {
            const tag = NODE_ERROR_CATEGORY_LABELS[err.category] || err.category;
            const location = err.locationLabel
                ? `<div class="node-error-popover-location">Where: <strong>${escapeHtml(err.locationLabel)}</strong></div>`
                : '';
            const fix = err.fix
                ? `<div class="node-error-popover-fix"><span>How to fix:</span> ${escapeHtml(err.fix)}</div>`
                : '';
            const jump = err.fieldSelector
                ? `<button type="button" class="node-error-popover-jump" data-jump-idx="${idx}">Jump to field</button>`
                : '';
            return `
                <li class="node-error-popover-item node-error-popover-item--${escapeHtml(err.category || 'configuration')}">
                    <span class="node-error-popover-tag">${escapeHtml(tag)}</span>
                    <div class="node-error-popover-item-title">${escapeHtml(err.title || 'Issue')}</div>
                    ${location}
                    <div class="node-error-popover-message">${escapeHtml(err.message || '')}</div>
                    ${fix}
                    ${jump}
                </li>
            `;
        }).join('');

        pop.innerHTML = `
            <div class="node-error-popover-header">
                <span class="node-error-popover-title">${errors.length} issue${errors.length === 1 ? '' : 's'} to fix</span>
                <button type="button" class="node-error-popover-close" aria-label="Close">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true">
                        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                </button>
            </div>
            <ul class="node-error-popover-list">${itemsHtml}</ul>
        `;

        document.body.appendChild(pop);
        this._errorPopover = pop;

        pop.querySelector('.node-error-popover-close')?.addEventListener('click', () => this.hideErrorPopover());
        pop.querySelectorAll('.node-error-popover-jump').forEach(btn => {
            btn.addEventListener('click', (e) => {
                e.stopPropagation();
                const idx = parseInt(btn.dataset.jumpIdx, 10);
                this.jumpToErrorField(nodeId, errors[idx]);
            });
        });

        this.positionErrorPopover(badgeEl, pop);
        this._errorPopoverReposition = () => this.positionErrorPopover(badgeEl, pop);
        window.addEventListener('resize', this._errorPopoverReposition);
        window.addEventListener('scroll', this._errorPopoverReposition, true);

        this._errorPopoverDismiss = (e) => {
            if (pop.contains(e.target) || badgeEl.contains(e.target)) return;
            this.hideErrorPopover();
        };
        // Defer attaching dismiss handler so the click that opened the
        // popover doesn't immediately close it via bubbling.
        setTimeout(() => document.addEventListener('click', this._errorPopoverDismiss), 0);
    }

    positionErrorPopover(badgeEl, popoverEl) {
        const rect = badgeEl.getBoundingClientRect();
        const popWidth = popoverEl.offsetWidth || 320;
        const popHeight = popoverEl.offsetHeight || 200;
        const viewportW = window.innerWidth;
        const viewportH = window.innerHeight;

        // Prefer right side, flip left if there isn't room.
        let left = rect.right + 12;
        if (left + popWidth > viewportW - 12) {
            left = Math.max(12, rect.left - popWidth - 12);
        }
        // Vertically clamp so the popover never spills off-screen.
        let top = Math.max(12, rect.top);
        if (top + popHeight > viewportH - 12) {
            top = Math.max(12, viewportH - popHeight - 12);
        }

        popoverEl.style.top = `${top}px`;
        popoverEl.style.left = `${left}px`;
    }

    hideErrorPopover() {
        if (this._errorPopover) {
            this._errorPopover.remove();
            this._errorPopover = null;
        }
        if (this._errorPopoverReposition) {
            window.removeEventListener('resize', this._errorPopoverReposition);
            window.removeEventListener('scroll', this._errorPopoverReposition, true);
            this._errorPopoverReposition = null;
        }
        if (this._errorPopoverDismiss) {
            document.removeEventListener('click', this._errorPopoverDismiss);
            this._errorPopoverDismiss = null;
        }
    }

    jumpToErrorField(nodeId, err) {
        const wrapper = document.getElementById(`node-${nodeId}`);
        if (!wrapper || !err?.fieldSelector) return;
        const target = wrapper.querySelector(err.fieldSelector);
        if (!target) return;

        target.scrollIntoView({ block: 'center', behavior: 'smooth' });
        target.classList.add('node-field-error-focus');
        setTimeout(() => target.classList.remove('node-field-error-focus'), 1500);
        if (typeof target.focus === 'function') {
            try { target.focus({ preventScroll: true }); } catch (_) { target.focus(); }
        }
        this.hideErrorPopover();
    }

    // Drops any error whose fieldSelector now points at a populated value,
    // so the badge updates in real time as the user fixes things instead of
    // waiting for the next Save click.
    handleNodeInputForErrorClear(target) {
        if (!this.nodeErrors || this.nodeErrors.size === 0) return;
        const wrapper = target.closest('[id^="node-"]');
        if (!wrapper) return;
        const nodeId = wrapper.id.replace('node-', '');
        const errors = this.nodeErrors.get(String(nodeId));
        if (!errors?.length) return;

        const remaining = errors.filter(err => {
            if (!err.fieldSelector) return true;
            const field = wrapper.querySelector(err.fieldSelector);
            if (!field) return true;
            // Only consider this error fixed if the change happened on the
            // exact field we flagged (avoids accidentally clearing one
            // error because the user touched a different input).
            if (field !== target && !(field.contains && field.contains(target))) return true;
            const val = (field.value || '').toString().trim();
            return val === '';
        });

        if (remaining.length === errors.length) return;
        this.nodeErrors.set(String(nodeId), remaining);
        this.applyNodeErrorVisuals(nodeId, remaining);
    }

    // Public alias retained for any caller that still flips a single node's
    // valid/invalid state. The "invalid" path is intentionally a no-op now:
    // ad-hoc invalidation would lack the structured metadata the popover
    // depends on, so all error creation must flow through validateSequence.
    updateNodeErrorState(nodeId, isValid = false) {
        if (isValid) {
            this.nodeErrors?.delete(String(nodeId));
            this.applyNodeErrorVisuals(nodeId, []);
        }
    }

    // Drops a single error code off a node (used by handlers that mutate
    // state programmatically — picking an email template, saving the SMS
    // body via Quill, applying trigger filters — none of which dispatch a
    // native input event on the hidden inputs that the auto-clear logic
    // watches).
    clearNodeErrorByCode(wrapperOrNode, code) {
        if (!this.nodeErrors) return;
        const wrapper = wrapperOrNode?.closest
            ? wrapperOrNode.closest('[id^="node-"]')
            : wrapperOrNode;
        if (!wrapper) return;
        const nodeId = wrapper.id.replace('node-', '');
        const errors = this.nodeErrors.get(String(nodeId));
        if (!errors?.length) return;
        const remaining = errors.filter(e => e.code !== code);
        if (remaining.length === errors.length) return;
        this.nodeErrors.set(String(nodeId), remaining);
        this.applyNodeErrorVisuals(nodeId, remaining);
    }

    syncTriggerFilterOperators(row) {
        const fieldSelect = row.querySelector('.trigger-field');
        const selectedOption = fieldSelect.options[fieldSelect.selectedIndex];
        const filterType = selectedOption?.getAttribute('data-filter-type') || '';

        const operatorSelect = row.querySelector('.trigger-operator');
        const valueInput = row.querySelector('.trigger-value');
        const dateRange = row.querySelector('.trigger-date-range');

        if (filterType === 'duration-range') {
            operatorSelect.style.display = 'none';
            if (valueInput) valueInput.style.display = 'none';
            if (dateRange) dateRange.style.display = 'flex';
            return;
        }

        if (dateRange) dateRange.style.display = 'none';

        // Show/hide operator options by filter type class
        const allOptions = operatorSelect.querySelectorAll('option');
        allOptions.forEach(opt => {
            opt.style.display = opt.classList.contains('op-' + filterType) ? '' : 'none';
            opt.selected = false;
        });

        // Select the first visible option
        const firstVisible = operatorSelect.querySelector('option[style=""], option:not([style])');
        if (firstVisible) firstVisible.selected = true;

        operatorSelect.style.display = filterType ? '' : 'none';

        // Show/hide value input based on operator
        const needsValue = ['string', 'integer', 'date', 'birthday', 'engagement-integer', 'trigger'];
        const noValueOperators = ['is unknown', 'has any value', 'has occurred'];
        const currentOp = operatorSelect.value;

        if (needsValue.includes(filterType) && !noValueOperators.includes(currentOp)) {
            valueInput.style.display = '';
            if (['integer', 'engagement-integer'].includes(filterType)) {
                valueInput.type = 'number';
                valueInput.placeholder = 'Value…';
            } else if (['date', 'birthday'].includes(filterType)) {
                valueInput.type = 'number';
                valueInput.placeholder = 'Days…';
            } else if (filterType === 'trigger') {
                valueInput.type = 'number';
                valueInput.placeholder = triggerValuePlaceholder(currentOp);
            } else {
                valueInput.type = 'text';
                valueInput.placeholder = 'Value…';
            }
        } else {
            valueInput.style.display = 'none';
        }
    }
}

// === Node error helpers (module-scoped) ===

// Categories the popover knows how to render. Keep these in sync with the
// `.node-error-popover-item--<category>` styles in campaigns.scss.
const NODE_ERROR_CATEGORY_LABELS = {
    configuration:      'Configuration',
    missing_data:       'Missing data',
    invalid_connection: 'Connection',
    external_service:   'External service'
};

// Tiny factory so every error object has a consistent `category` field
// without callers having to remember to set it. Each branch just spreads
// the rest of the metadata (title, message, fieldSelector, fix, …).
const NodeError = {
    configuration:      (data) => ({ category: 'configuration', ...data }),
    missing_data:       (data) => ({ category: 'missing_data', ...data }),
    invalid_connection: (data) => ({ category: 'invalid_connection', ...data }),
    external_service:   (data) => ({ category: 'external_service', ...data })
};

// HTML-escape user-visible strings before they go through innerHTML. The
// only fields that ever round-trip user-authored text are field labels
// and dynamic value snippets (custom sender email, etc.), so escaping is
// mandatory to avoid breakage / XSS via campaign content.
function escapeHtml(s) {
    if (s == null) return '';
    return String(s).replace(/[&<>"']/g, c => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
}

// Maps an `op-trigger` operator value to the unit the user is being asked
// for, so the placeholder reflects times/days/hours rather than a generic
// "Value…". Kept module-scoped because the two `applyTriggerFilterTypeToRow`
// twins both call it and we'd otherwise duplicate the mapping.
function triggerValuePlaceholder(op) {
    if (op === 'greater than' || op === 'less than') return 'Times…';
    if (op && op.endsWith('hours')) return 'Hours…';
    return 'Days…';
}

// Filter types that surface a free-text value input next to the operator
// dropdown. Everything else (gender, provider, review, is_subscribed,
// sms_subscribed, location, guest_tag) treats the operator option itself
// as the value, so no input field should ever be shown.
const TRIGGER_VALUE_INPUT_FILTER_TYPES = ['string', 'integer', 'date', 'birthday', 'engagement-integer', 'trigger'];

// Operators that complete the filter on their own — "is unknown" / "has any
// value" / "has occurred" — so even on a value-input filter type the input
// should stay hidden.
const TRIGGER_NO_VALUE_OPERATORS = ['is unknown', 'has any value', 'has occurred'];

function triggerOperatorNeedsValueInput(filterType, operator) {
    return TRIGGER_VALUE_INPUT_FILTER_TYPES.includes(filterType)
        && !TRIGGER_NO_VALUE_OPERATORS.includes(operator || '');
}

// Tunes the input's HTML type and placeholder to match what the operator
// is asking for (e.g. birthday "more than _ days ago" → number / "Days…").
function applyTriggerValueInputType(input, filterType, operator) {
    if (!input) return;
    if (filterType === 'trigger') {
        input.type = 'number';
        input.placeholder = triggerValuePlaceholder(operator);
    } else if (filterType === 'date' || filterType === 'birthday') {
        input.type = 'number';
        input.placeholder = operator && operator.endsWith('hours') ? 'Hours…' : 'Days…';
    } else if (filterType === 'integer' || filterType === 'engagement-integer') {
        input.type = 'number';
        input.placeholder = 'Value…';
    } else {
        input.type = 'text';
        input.placeholder = 'Value…';
    }
}
